import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Save, Trash2, ImageUp, Plus, X } from 'lucide-react';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardBody, CardHeader } from '@/shared/components/ui/Card';
import { Input } from '@/shared/components/ui/Input';
import { Badge } from '@/shared/components/ui/Badge';
import { Skeleton } from '@/shared/components/ui/Skeleton';
import { getApiErrorMessage } from '@/shared/api/axios';
import {
  useBranding, useUpdateBranding, useUploadArtwork, useClearArtwork,
  type Branding, type ArtworkSlot,
} from '../document-templates/api';

type IdentityForm = Pick<
  Branding,
  'companyName' | 'legalName' | 'cin' | 'addressLine' | 'city' | 'state' | 'pincode'
  | 'country' | 'email' | 'phone' | 'website' | 'signatoryName' | 'signatoryTitle'
> & { socials: { label: string; url: string }[] };

const EMPTY: IdentityForm = {
  companyName: '', legalName: '', cin: '', addressLine: '', city: '', state: '', pincode: '',
  country: '', email: '', phone: '', website: '', signatoryName: '', signatoryTitle: '', socials: [],
};

const ARTWORK_LABELS: Record<ArtworkSlot, string> = {
  header: 'Header artwork (top of every page)',
  footerFull: 'Detailed footer (final page)',
  footerCompact: 'Compact footer (earlier pages)',
};

export function HrBrandingPage() {
  const { data, isLoading, isError } = useBranding();
  const updateMut = useUpdateBranding();
  const uploadMut = useUploadArtwork();
  const clearMut = useClearArtwork();

  const [form, setForm] = useState<IdentityForm>(EMPTY);
  const loadedRef = useRef(false);

  useEffect(() => {
    if (data?.branding && !loadedRef.current) {
      loadedRef.current = true;
      const b = data.branding;
      setForm({
        companyName: b.companyName, legalName: b.legalName, cin: b.cin, addressLine: b.addressLine,
        city: b.city, state: b.state, pincode: b.pincode, country: b.country, email: b.email,
        phone: b.phone, website: b.website, signatoryName: b.signatoryName, signatoryTitle: b.signatoryTitle,
        socials: b.socials ?? [],
      });
    }
  }, [data]);

  const set = <K extends keyof IdentityForm>(key: K, value: IdentityForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  async function handleSave() {
    try {
      await updateMut.mutateAsync(form);
      toast.success('Branding saved.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to save branding.'));
    }
  }

  async function handleUpload(slot: ArtworkSlot, file: File | undefined) {
    if (!file) return;
    try {
      await uploadMut.mutateAsync({ slot, file });
      toast.success('Artwork updated.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to upload artwork.'));
    }
  }

  async function handleClear(slot: ArtworkSlot) {
    try {
      await clearMut.mutateAsync(slot);
      toast.success('Reverted to default artwork.');
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to clear artwork.'));
    }
  }

  if (isLoading) return <div><Skeleton className="h-96" /></div>;
  if (isError || !data) return <div><p className="text-sm text-danger">Failed to load branding.</p></div>;

  const branding = data.branding;

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-primary">Letterhead Branding</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Company identity and artwork applied to every generated HR document.
          </p>
        </div>
        <Badge tone={data.isCustomized ? 'success' : 'neutral'}>
          {data.isCustomized ? 'Customized' : 'Using ZYNX default'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Identity */}
        <Card>
          <CardHeader title="Company identity" action={<Button size="sm" onClick={handleSave} loading={updateMut.isPending}><Save className="h-4 w-4 mr-1" />Save</Button>} />
          <CardBody className="space-y-3">
            <Input label="Company name" value={form.companyName} onChange={(e) => set('companyName', e.target.value)} placeholder="Your company name" />
            <Input label="Legal name" value={form.legalName} onChange={(e) => set('legalName', e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="CIN" value={form.cin} onChange={(e) => set('cin', e.target.value)} />
              <Input label="Registered city" value={form.city} onChange={(e) => set('city', e.target.value)} />
            </div>
            <Input label="Address line" value={form.addressLine} onChange={(e) => set('addressLine', e.target.value)} />
            <div className="grid grid-cols-3 gap-3">
              <Input label="State" value={form.state} onChange={(e) => set('state', e.target.value)} />
              <Input label="Pincode" value={form.pincode} onChange={(e) => set('pincode', e.target.value)} />
              <Input label="Country" value={form.country} onChange={(e) => set('country', e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input label="Email" value={form.email} onChange={(e) => set('email', e.target.value)} />
              <Input label="Phone" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
            </div>
            <Input label="Website" value={form.website} onChange={(e) => set('website', e.target.value)} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Signatory name" value={form.signatoryName} onChange={(e) => set('signatoryName', e.target.value)} />
              <Input label="Signatory title" value={form.signatoryTitle} onChange={(e) => set('signatoryTitle', e.target.value)} />
            </div>

            {/* Socials */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-body-sm font-medium text-on-surface">Social links</span>
                <Button size="sm" variant="ghost" onClick={() => set('socials', [...form.socials, { label: '', url: '' }])}>
                  <Plus className="h-4 w-4 mr-1" />Add
                </Button>
              </div>
              <div className="space-y-2">
                {form.socials.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      className="h-9 w-32 rounded-DEFAULT border border-outline bg-surface px-2 text-data-md"
                      placeholder="Label"
                      value={s.label}
                      onChange={(e) => set('socials', form.socials.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
                    />
                    <input
                      className="h-9 flex-1 rounded-DEFAULT border border-outline bg-surface px-2 text-data-md"
                      placeholder="https://…"
                      value={s.url}
                      onChange={(e) => set('socials', form.socials.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                    />
                    <Button size="icon" variant="ghost" onClick={() => set('socials', form.socials.filter((_, j) => j !== i))} aria-label="Remove social link">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Artwork + preview */}
        <div className="space-y-4">
          <Card>
            <CardHeader title="Artwork" subtitle="PNG, JPEG, or WebP up to 3MB. Blank uses the ZYNX default." />
            <CardBody className="space-y-4">
              {(Object.keys(ARTWORK_LABELS) as ArtworkSlot[]).map((slot) => (
                <ArtworkRow
                  key={slot}
                  slot={slot}
                  label={ARTWORK_LABELS[slot]}
                  src={branding[slot]?.src}
                  uploading={uploadMut.isPending}
                  onUpload={(file) => handleUpload(slot, file)}
                  onClear={() => handleClear(slot)}
                />
              ))}
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Live letterhead preview" />
            <CardBody>
              <LetterheadPreview branding={branding} form={form} />
            </CardBody>
          </Card>
        </div>
      </div>
    </div>
  );
}

function ArtworkRow({
  slot, label, src, uploading, onUpload, onClear,
}: {
  slot: ArtworkSlot;
  label: string;
  src?: string;
  uploading: boolean;
  onUpload: (file: File | undefined) => void;
  onClear: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <div className="rounded-md border border-outline p-3">
      <div className="flex items-center justify-between mb-2">
        <span className="text-body-sm font-medium text-on-surface">{label}</span>
        <div className="flex gap-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(e) => { onUpload(e.target.files?.[0]); e.target.value = ''; }}
          />
          <Button size="sm" variant="outline" onClick={() => inputRef.current?.click()} loading={uploading}>
            <ImageUp className="h-4 w-4 mr-1" />Upload
          </Button>
          <Button size="sm" variant="ghost" onClick={onClear} aria-label={`Clear ${slot} artwork`}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      {src ? (
        <img src={src} alt={label} className="max-h-24 w-full object-contain rounded border border-outline bg-white" />
      ) : (
        <p className="text-xs text-muted-foreground">No custom artwork.</p>
      )}
    </div>
  );
}

/** Client-side letterhead mock so the user sees identity + artwork changes live. */
function LetterheadPreview({ branding, form }: { branding: Branding; form: IdentityForm }) {
  const footerLines = [
    form.cin ? `CIN: ${form.cin}` : '',
    [form.addressLine, form.city, form.state, form.pincode].filter(Boolean).join(', '),
    [form.email, form.phone, form.website].filter(Boolean).join(' · '),
  ].filter(Boolean);

  return (
    <div className="mx-auto w-full max-w-md border border-outline bg-white shadow-surface-1">
      {branding.header?.src && (
        <img src={branding.header.src} alt="Letterhead header" className="w-full block" />
      )}
      <div className="px-6 py-5 text-gray-800">
        <h4 className="text-center font-semibold text-sm">SAMPLE DOCUMENT</h4>
        <p className="mt-2 text-xs leading-relaxed">
          This is a live preview of the letterhead applied to every generated HR document for
          <strong> {form.companyName || 'your company'}</strong>. The body content of each document
          comes from its template.
        </p>
      </div>
      {branding.footerFull?.src ? (
        <img src={branding.footerFull.src} alt="Letterhead footer" className="w-full block" />
      ) : (
        <div className="border-t-2 border-primary/70 px-6 py-3 text-center text-[10px] leading-relaxed text-gray-600">
          <div className="font-semibold text-primary">{form.legalName || form.companyName || 'Your Company'}</div>
          {footerLines.map((line, i) => <div key={i}>{line}</div>)}
        </div>
      )}
    </div>
  );
}
